// Context hooks - replacing Apollo
import { useResult } from "../../../../shared/formContext/hooks/useResult";

// Components
import Box from "../../../../components/Box/Box";
import Cta from "../../../../components/Cta/Cta";
import IconContent from "../../../../components/IconContent/IconContent";
import Tabs from "../../../../components/Tabs/Tabs";

// Tabs
import TabSavingsComposition from "../Tabs/TabSavingsComposition";
import TabSavingsEvolution from "../Tabs/TabSavingsEvolution";
import ShareFullLink from "../../../../shared/components/ShareFullLink";

// Helpers
import { utils } from "../../../../helpers";
//import CopySVG from "../../../../../assets/media/svg/icons/copy.svg";
//import CheckSVG from "../../../../../assets/media/svg/icons/check.svg";

// WP i18n
import { __ } from "@wordpress/i18n";
import { SavingsCertificatesResult } from "@blocks/savings-certificates/interfaces/savingsCertificatesTypes.ts";


/**
 * BiG Bank URL for banner (with UTM tracking)
 */
const BIG_BANK_BANNER_URL = "https://www.big.pt/?utm_source=doutorfinancas&utm_medium=simulador";

/**
 * SavingsCertificates Component
 *
 * Main component for displaying savings certificates simulation results
 * Uses Context API instead of Apollo for state management
 * Follows the project's standard layout pattern with Box, IconContent, Tabs
 * Includes clickable BiG bank elements (logo and banner)
 *
 * @returns JSX element containing the complete savings certificates results display
 */
const SavingsCertificates: React.FC = () => {
  // Use Context hook instead of Apollo reactive variables
  const { resultData } = useResult();
  const { response, loading } = resultData;

  // Extract summary data from API response
  const apiResult = response?.data.result as SavingsCertificatesResult;

  const handleSavingsClick = () => {
    window.open(BIG_BANK_BANNER_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="simulador-certificados-de-aforro">
      {/* Main Results Box - Following project standard pattern */}
      {response && apiResult && (
        <Box
          grid
          loading={loading}
          header={{
            title: __(
              "O montante líquido disponível no final é:",
              "dfs-tools",
            ),
            info: utils.toEuro(apiResult.net_amount || 0),
            color: "orange-100",
          }}
        >
          <IconContent
            title={__("Deste valor, o capital subscrito é de:", "df-simulators")}
            color="orange-100"
            bigText={utils.toEuro(apiResult.total_invested || 0)}
          />
          <IconContent
            title={__("Juros líquidos acumulados:", "df-simulators")}
            color="orange-100"
            bigText={utils.toEuro(apiResult.net_interest_amount || 0)}
          />
          <IconContent
            title={__("Taxa de retorno líquida anualizada:", "df-simulators")}
            color="orange-100"
            bigText={`${utils.toRound(apiResult.irr_net || 0, 2)}%`}
          />
        </Box>
      )}

      {/* Tabs for Composition and Evolution */}
      <Tabs
        loading={loading}
        data={[
          {
            heading: __("Composição do valor subscrito", "df-simulators"),
            content: <TabSavingsComposition />,
          },
          {
            heading: __("Evolução do valor subscrito", "df-simulators"),
            content: <TabSavingsEvolution />,
          },
        ]}
      />

      <ShareFullLink />

      {/* Notes Box - Following project standard pattern */}
      <Box
        backgroundColor="navy-5"
        textColor="navy-70"
        vertical
        header={{
          title: __("Notas", "df-simulators"),
          textColor: "navy-70",
        }}
      >
        <IconContent
          title=""
          text={`
            <ul>
              <li>A taxa Euribor a 3 meses utilizada pode ser configurada pelo utilizador e é assumida como constante durante todo o período da simulação. Dados do Banco de Portugal revelam que, entre 2021 e 2025, a média mensal da Euribor 3 meses esteve entre -0,582% e 3,968%. Consulte a taxa Euribor atualmente vigente <a href="https://bpstat.bportugal.pt/serie/13168436" target="_blank" rel="noopener noreferrer" class="underline">aqui</a> ou a <a href="https://www.igcp.pt/pt/aforristas/produtos-de-aforro/certificados-de-aforro" target="_blank" rel="noopener noreferrer" class="underline">taxa de juro base divulgada pelo IGCP</a>.</li>
              <li>A taxa de juro aplicável a cada subscrição resulta da soma da taxa de juro base, determinada pela Euribor a 3 meses, com o prémio de permanência correspondente, que aumenta progressivamente ao longo do tempo.</li>
              <li>A tributação é aplicada às taxas de IRS em vigor à data da simulação.</li>
              <li>Cada subscrição, inicial ou periódica, é tratada como um certificado separado, com cálculo próprio de juros e rentabilidade. Os juros são capitalizados trimestralmente, sempre com referência à data do último apuramento de juros.</li>
              <li>A taxa de retorno líquida anualizada é apenas uma estimativa e não constitui garantia de rentabilidade futura.</li>
              <li>A simulação destina-se apenas a fins informativos, para obter informação completa acerca dos Certificados de Aforro Série F consulte a <a href="https://www.big.pt/pdf/igcp/CAF_Serie_F.pdf" target="_blank" rel="noopener noreferrer" class="underline">Ficha de Produto</a>.</li>
            </ul>
          `}
          color="navy-70"
          textColor="navy-70"
        />
        <IconContent
          title={__("Aviso legal:", "df-simulators")}
          text={__(
            'Esta simulação é meramente informativa e indicativa, não substituindo uma análise de investimento personalizada pelo investidor, que deverá considerar as suas especificidades, nomeadamente em matéria de regime fiscal e condições financeiras. A presente simulação não dispensa a consulta e análise da Ficha de Produto pelo investidor.<br>Banco de Investimento Global, S.A., registado no Banco de Portugal, com o número 61, na CMVM com o número de registo 263 e na Autoridade de Supervisão de Seguros e Fundos de Pensões (ASF), como agente de seguros nº 419501242.',
            "df-simulators",
          )}
          color="navy-70"
          textColor="navy-70"
        />
      </Box>

      {/* Clickable BiG Banner/Display Ad */}
      <div className="partnership-info mt-6">
        <button
          onClick={handleSavingsClick}
          className="cursor-pointer hover:opacity-90 transition-opacity duration-200 bg-transparent border-none p-0 w-full"
          aria-label={__("Visitar website do BiG", "df-simulators")}
        >
          <img
            src="/wp-content/plugins/df-simulators/src/blocks/savings-certificates/assets/img/Doutor-Financas-Simulador-Certificados-Aforro-Display-Ad-BiG.webp"
            alt="No BiG os Certificados de Aforro estão à distância de um clique"
            className="w-full rounded-lg"
          />
        </button>
      </div>

      {/* Call-to-Action - Standard project pattern */}
      <Cta />
    </div>
  );
};

export default SavingsCertificates;
